import { Link } from "react-router-dom";
import { BlocksRenderer, type BlocksContent } from "@strapi/blocks-react-renderer";
import { motion } from "framer-motion";
import { Card, CardContent as CardComponentContent } from "@/components/ui/card";

import { CardInfo as InfoType } from "@/types/card";
import * as Icons from "lucide-react";
import { LucideIcon } from "lucide-react";

interface CardInfoProps extends InfoType {
	index: number;
}

export default function CardInfo({ cardIcon, index, cardTitle, cardContent, textAlignment, shouldStandOut }: CardInfoProps) {
	const renderIcon = (iconName: string): React.ReactElement => {

		const IconComponent = Icons[iconName as keyof typeof Icons] as LucideIcon | undefined;
		if (IconComponent) {
			return <IconComponent className={`w-6 h-6 text-primary-500 ${shouldStandOut ? 'text-yellow-700' : ''}`} />;
		}
		return <Icons.Star className={`w-6 h-6 text-primary-500 ${shouldStandOut ? 'text-yellow-700' : ''}`} />;
	}

	const sectionAlignment = textAlignment === "center" ? "text-center" : '';

	const iconContainerClasses = textAlignment === "center"
		? "w-16 h-16 mx-auto mb-6 bg-primary-100" + (shouldStandOut ? " bg-yellow-100" : "")
		: "w-12 h-12 flex-shrink-0 bg-primary-50" + (shouldStandOut ? " bg-yellow-100" : "");


	return (
		<section className={`py-12 px-4 ${shouldStandOut ? 'bg-yellow-50' : ''}`}>
			<div className={`max-w-4xl mx-auto space-y-8 ${sectionAlignment}`}>
				<motion.div
					key={index}
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: index * 0.1 }}
					viewport={{ once: true }}
				>
					<Card className="glass-card border-0 shadow-lg">
						<CardComponentContent className="p-8">
							<div className={`flex space-x-4 mb-6 ${textAlignment === "center" ? "justify-center items-center flex-col" : "items-start"}`}>
								{
									cardIcon && (
										<div className={`rounded-full flex items-center justify-center ${iconContainerClasses}`}>
											{renderIcon(cardIcon as string)}
										</div>
									)
								}
								<h2 className={`text-2xl font-bold font-karla ${shouldStandOut ? 'text-yellow-900' : 'text-gray-900'}`}>
									{cardTitle}
								</h2>
							</div>

							{cardContent && (
								<div className={`${shouldStandOut ? 'text-yellow-800' : 'text-gray-600'} font-karla leading-relaxed space-y-4`}>
									<BlocksRenderer
										content={cardContent as BlocksContent}
										blocks={{
											list: ({ children }) => <ul className="space-y-3">{children}</ul>,
											link: ({ children, url }) => <Link to={url} className={`${shouldStandOut ? 'text-yellow-900 hover:text-yellow-950' : 'text-primary-600 hover:text-primary-700'} underline`}>{children}</Link>,
										}}
									/>
								</div>
							)}
						</CardComponentContent>
					</Card>
				</motion.div>
			</div>
		</section>
	)
}